import { useState, useEffect, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { app as teamsApp, authentication } from "@microsoft/teams-js";
import { loginRequest } from "./authConfig_new";

/* Decodifica únicamente el payload de un JWT (base64-URL → JSON) */
function decodeJwtPayload(jwt) {
  const base64 = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(atob(base64));
}

/**
 * Hook de autenticación unificada para aplicaciones React.
 *
 * Soporta tres flujos, expuestos como helpers independientes y un método
 * `login()` que los encadena en orden de prioridad (popup → silent → context):
 *   • loginPopup   → MSAL loginPopup()                                (web & Teams-web)
 *   • loginContext → Teams getAuthToken() + bootstrap ssoSilent()     (Teams desktop/mobile)
 *   • login()      → Intenta popup, luego ssoSilent, finalmente contexto
 *
 * El estado final de sesión se expone en `user`, `loading`, `error` y helpers
 * `logout()` y `getToken(scopes)`.
 */
export function useUnifiedAuth() {
  const { instance, inProgress } = useMsal();
  const initRef = useRef(null);

  /* Garantiza llamada única a instance.initialize() */
  const ensureInitialized = () => {
    if (!initRef.current) {
      initRef.current = instance.initialize();
    }
    return initRef.current;
  };

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* Restaurar sesión MSAL (si existe) al montar */
  useEffect(() => {
    (async () => {
      await ensureInitialized();
      const acct = instance.getActiveAccount() || instance.getAllAccounts()[0];
      if (acct) {
        // setUser({
        //   username: acct.username,
        //   name: acct.name ?? acct.username,
        //   tenantId: acct.tenantId ?? "(n/a)",
        //   method: "MSAL Restored",
        // });
        const objectId = acct.homeAccountId.split('.')[0];
        setUser({
          username: acct.username,
          name:     acct.name     ?? acct.username,
          tenantId: acct.tenantId ?? "(n/a)",
          id:       objectId,
          method:   "MSAL Restored",
        });
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------------------------------------------------- */
  /*  Helpers                                                   */
  /* ---------------------------------------------------------- */
  const setUserFromAccount = (account, method = "MSAL") => {
    if (!account) return;
    // const u = {
    //   username: account.username,
    //   name:     account.name     || account.username,
    //   tenantId: account.tenantId || "(n/a)",
    //   id:       account.homeAccountId,
    //   method,
    // };
    // solo objectId, no el tenantId concatenado
    const objectId = account.homeAccountId.split('.')[0];
    const u = {
      username: account.username,
      name:     account.name     || account.username,
      tenantId: account.tenantId || "(n/a)",
      id:       objectId,
      method,
    };
    setUser(u);
    // console.log("🔐 [UnifiedAuth] setUserFromAccount →", u);
  };

  /* ---------------------------------------------------------- */
  /*  Método (1) MSAL loginPopup()                              */
  /* ---------------------------------------------------------- */
  const loginPopup = useCallback(async () => {
    setLoading(true);
    setError(null);
    await ensureInitialized();
    try {
      const resp = await instance.loginPopup(loginRequest);
      instance.setActiveAccount(resp.account);
      setUserFromAccount(resp.account, "MSAL Popup");
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err; // lo re-lanzamos para seguir con otro método
    } finally {
      setLoading(false);
    }
  }, [instance]);

  /* ---------------------------------------------------------- */
  /*  Método (2) Context Teams + getAuthToken()                  */
  /* ---------------------------------------------------------- */
  const loginContext = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await teamsApp.initialize();
      const ssoToken = await authentication.getAuthToken({});
      const jwt = decodeJwtPayload(ssoToken);
      const email = jwt.preferred_username || jwt.upn;
      const name = jwt.name || email;
      const tenantId = jwt.tid;
      const objectId = jwt.oid || jwt.sub;

      // setUser({
      //   username: email,
      //   name,
      //   tenantId,
      //   method: "Teams Context",
      // });
      setUser({
        username: email,
        name,
        tenantId,
        id:       objectId,
        method:   "Teams Context",
      });
      /* Bootstrap MSAL SSO silently para futuras llamadas */
      await ensureInitialized();
      instance
        .ssoSilent({
          ...loginRequest,
          loginHint: email,
          prompt: "none",
        })
        .then((res) => {
          instance.setActiveAccount(res.account);
          console.info("[UnifiedAuth] ssoSilent bootstrap OK →", res.account.username);
        })
        .catch((silentErr) => {
          console.warn("[UnifiedAuth] ssoSilent bootstrap failed →", silentErr);
        });
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [instance]);

  /* ---------------------------------------------------------- */
  /*  login() encadenado: popup → ssoSilent → contexto          */
  /* ---------------------------------------------------------- */
  const login = useCallback(async () => {
    /* 1️⃣ MSAL Popup */
    try {
      await loginPopup();
      return;
    } catch (popupErr) {
      console.warn("[UnifiedAuth] loginPopup falló, intento ssoSilent", popupErr);
    }

    /* 2️⃣ MSAL ssoSilent */
    setLoading(true);
    try {
      await ensureInitialized();
      const silentResp = await instance.ssoSilent({
        ...loginRequest,
        loginHint: instance.getAllAccounts()[0]?.username,
        prompt: "none",
      });
      instance.setActiveAccount(silentResp.account);
      setUserFromAccount(silentResp.account, "MSAL Silent");
      return;
    } catch (silentErr) {
      console.warn("[UnifiedAuth] ssoSilent falló, intento contexto", silentErr);
    } finally {
      setLoading(false);
    }

    /* 3️⃣ Contexto Teams */
    try {
      await loginContext();
    } catch (ctxErr) {
      console.error("[UnifiedAuth] Context login falló también:", ctxErr);
      /* error ya seteado en loginContext */
    }
  }, [loginPopup, loginContext, instance]);

  /* ---------------------------------------------------------- */
  /*  logout(): MSAL + limpieza local                           */
  /* ---------------------------------------------------------- */
  const logout = useCallback(async () => {
    await ensureInitialized();
    try {
      await instance.logoutPopup({ postLogoutRedirectUri: "/" });
    } finally {
      setUser(null);
    }
  }, [instance]);

  /* ---------------------------------------------------------- */
  /*  getToken(scopes): Acquire token con scopes específicos     */
  /* ---------------------------------------------------------- */
  const getToken = useCallback(
    async (scopes) => {
      await ensureInitialized();
      try {
        const resp = await instance.acquireTokenSilent({
          ...loginRequest,
          scopes,
        });
        return resp.accessToken;
      } catch (err) {
        if (err.errorCode === "interaction_required") {
          const resp = await instance.acquireTokenPopup({
            ...loginRequest,
            scopes,
          });
          return resp.accessToken;
        }
        throw err;
      }
    },
    [instance],
  );

  /* ---------------------------------------------------------- */
  /*  API pública del hook                                      */
  /* ---------------------------------------------------------- */
  return {
    user,
    loading: loading || inProgress !== "none",
    error,
    // helpers
    login,        // cascada completa
    loginPopup,   // explícito
    loginContext, // explícito
    logout,
    getToken,
  };
}